/**
 * KPI Dashboard Page
 * Display key metrics for investor reporting with drill-down capability
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  TrendingUp,
  Users,
  Video,
  Gamepad2,
  Download,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
  Activity,
  X,
  Eye,
  EyeOff,
  FileSpreadsheet,
  ChevronRight
} from 'lucide-react';

// Emerald Icon Component
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="emeraldGradientAdmin" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#emeraldGradientAdmin)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#6ee7b7" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#059669" fillOpacity="0.4" />
  </svg>
);

// Clickable Metric Card with drill-down capability
const MetricCard = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend, 
  trendValue, 
  color = 'emerald',
  onClick,
  dataKey,
  exportable = true
}) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-emerald-600',
    rose: 'from-rose-500 to-rose-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
  };

  const hoverClasses = {
    emerald: 'hover:border-emerald-300 hover:shadow-emerald-100',
    rose: 'hover:border-rose-300 hover:shadow-rose-100',
    blue: 'hover:border-blue-300 hover:shadow-blue-100',
    amber: 'hover:border-amber-300 hover:shadow-amber-100',
  };

  return (
    <div 
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer transition-all duration-200 ${hoverClasses[color]} hover:shadow-lg`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            {exportable && (
              <Eye className="w-3 h-3 text-gray-400" />
            )}
          </div>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend === 'up' ? (
                <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-rose-500" />
              )}
              <span className={`text-sm font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                {trendValue}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-50">
        <p className="text-xs text-gray-400 flex items-center gap-1">
          Click to view details <ChevronRight className="w-3 h-3" />
        </p>
      </div>
    </div>
  );
};

// Detail Modal Component
const DetailModal = ({ isOpen, onClose, title, data, category, onExport }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">Detailed breakdown and logs</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onExport}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export to CSV
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {data && data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {Object.keys(data[0]).map((key) => (
                      <th key={key} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="py-3 px-4 text-sm text-gray-600">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No detailed data available for this metric</p>
              <p className="text-sm text-gray-400 mt-1">Data will appear as activity increases</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KPIPage = () => {
  const { api } = useAdmin();
  const [overview, setOverview] = useState(null);
  const [streamKpis, setStreamKpis] = useState(null);
  const [gameKpis, setGameKpis] = useState(null);
  const [appStats, setAppStats] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [includeAll, setIncludeAll] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [modalCategory, setModalCategory] = useState('');

  useEffect(() => {
    fetchAllKpis();
  }, [period, includeAll]);

  const fetchAllKpis = async () => {
    try {
      setLoading(true);
      const params = includeAll ? 'include_all=true' : '';
      // Round 22: allSettled — one failing endpoint used to zero the WHOLE
      // page (Promise.all rejected before any setter ran). Now every section
      // that loaded renders, and the failures are named.
      const calls = [
        ['overview', () => api.get(`/api/admin/kpi/overview?${params}`), setOverview],
        ['streams', () => api.get(`/api/admin/kpi/streams?days=${period}`), setStreamKpis],
        ['games', () => api.get(`/api/admin/kpi/games?days=${period}`), setGameKpis],
        ['app-store', () => api.get(`/api/admin/app-store/stats?days=${period}`), setAppStats],
        ['accounts', () => api.get('/api/admin/accounts/breakdown'), setBreakdown],
      ];
      const results = await Promise.allSettled(calls.map(([, call]) => call()));
      const failed = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') calls[i][2](r.value.data);
        else failed.push(`${calls[i][0]} (${r.reason?.response?.status || 'network'})`);
      });
      if (failed.length) toast.error(`Could not load: ${failed.join(', ')}`);
    } catch (error) {
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV helper
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','), // Header row
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle values with commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return typeof value === 'object' ? JSON.stringify(value) : value;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV successfully!');
  };

  // Fetch detailed data for a specific metric
  const fetchMetricDetails = async (metricType, title) => {
    setModalTitle(title);
    setModalCategory(metricType);
    
    try {
      let detailData = [];
      
      switch (metricType) {
        case 'total_users':
        case 'dau':
        case 'mau':
        case 'new_signups':
          // Fetch user list for user-related metrics
          const usersRes = await api.get('/api/admin/users?page_size=100');
          detailData = usersRes.data.users.map(u => ({
            Name: u.name || 'N/A',
            Email: u.email || 'N/A',
            Handle: u.handle || 'N/A',
            Joined: u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A',
            Status: u.is_suspended ? 'Suspended' : 'Active',
            Wins: u.wins || 0,
            Losses: u.losses || 0
          }));
          break;
          
        case 'active_streams':
        case 'streams_today':
        case 'total_streams':
          // Fetch streams for stream-related metrics
          const streamsRes = await api.get('/api/admin/streams/live');
          const archivedRes = await api.get('/api/admin/streams/archived?page_size=50');
          const allStreams = [...(streamsRes.data.streams || []), ...(archivedRes.data.streams || [])];
          detailData = allStreams.map(s => ({
            Title: s.title || 'Untitled',
            Streamer: s.streamer?.name || s.user_id || 'Unknown',
            Viewers: s.viewer_count || 0,
            'Peak Viewers': s.peak_viewers || s.viewer_count || 0,
            Status: s.is_active ? 'Live' : 'Ended',
            Started: s.created_at ? new Date(s.created_at).toLocaleString() : 'N/A'
          }));
          break;
          
        case 'games_today':
        case 'total_games':
        case 'completed_games':
          // Fetch games for game-related metrics
          const gamesRes = await api.get('/api/admin/games/active');
          detailData = (gamesRes.data.games || []).map(g => ({
            'Game ID': g.id?.substring(0, 8) || 'N/A',
            Phase: g.phase || 'N/A',
            Players: g.players?.length || 0,
            'Max Players': g.max_players || 4,
            Entry: g.wager_amount || 0,
            Round: g.current_round || 1,
            Created: g.created_at ? new Date(g.created_at).toLocaleString() : 'N/A'
          }));
          break;
          
        case 'top_streamers':
          // Use the existing top streamers data
          detailData = (streamKpis?.top_streamers || []).map(s => ({
            Name: s.user?.name || 'Unknown',
            Handle: s.user?.handle || 'N/A',
            'Stream Count': s.stream_count || 0
          }));
          break;
          
        case 'retention_rate':
          // Create retention breakdown
          detailData = [
            { Metric: 'Total Users', Value: overview?.total_users || 0 },
            { Metric: 'Monthly Active Users', Value: overview?.mau || 0 },
            { Metric: 'Daily Active Users', Value: overview?.dau || 0 },
            { Metric: '7-Day Retention Rate', Value: `${overview?.retention_rate || 0}%` },
            { Metric: 'DAU/MAU Ratio', Value: overview?.mau ? `${Math.round((overview?.dau / overview?.mau) * 100)}%` : '0%' }
          ];
          break;
        
        case 'app_installs': {
          // Round 40: devices, by store (each store's doors counted together)
          const i = appStats?.installs || {};
          const row = (label, p) => ([
            { Store: label, Metric: 'Installs (devices)', Value: p?.total ?? 0 },
            { Store: label, Metric: `New in ${period} days`, Value: p?.new_in_period ?? 0 },
            { Store: label, Metric: 'Devices with the app id', Value: p?.devices ?? 0 },
            { Store: label, Metric: '…of which nobody has signed in yet', Value: p?.not_signed_in ?? 0 },
            { Store: label, Metric: 'Members on older app builds (no device id yet)', Value: p?.members_on_older_builds ?? 0 },
          ]);
          detailData = [
            ...row('iPhone & iPad (App Store + TestFlight)', i.ios),
            ...row('Android (Google Play + testing tracks)', i.android),
            { Store: 'All', Metric: 'Total installs', Value: i.total ?? 0 },
            { Store: 'Note', Metric: 'Official store downloads', Value: 'App Store Connect → Analytics / TestFlight, and Play Console → Statistics keep the stores’ own counts.' },
          ];
          break;
        }

        case 'app_users':
          // Round 22: members who have opened the app, by platform
          detailData = [
            { Metric: 'iOS app users', Value: appStats?.app_users?.ios || 0 },
            { Metric: 'Android app users', Value: appStats?.app_users?.android || 0 },
            { Metric: 'App users (either platform)', Value: appStats?.app_users?.total || 0 },
            { Metric: `New app users (${period} days)`, Value: appStats?.app_users?.new_in_period || 0 },
            { Metric: 'Note', Value: 'Store install counts live in App Store Connect / Play Console; this is first opens of the app by real accounts.' },
          ];
          break;

        case 'app_downloads':
        case 'google_play':
        case 'apple_store':
          // Fetch detailed click data
          const clicksRes = await api.get('/api/admin/app-store/clicks?page_size=100');
          detailData = (clicksRes.data.clicks || []).map(c => ({
            Store: c.store_type === 'google_play' ? 'Google Play' : 'Apple Store',
            'Click Time': c.timestamp ? new Date(c.timestamp).toLocaleString() : 'N/A',
            Page: c.page || 'N/A',
            Referrer: c.referrer || 'Direct',
            'User Agent': c.user_agent?.substring(0, 50) || 'N/A'
          }));
          break;
          
        default:
          detailData = [];
      }
      
      setModalData(detailData);
      setModalOpen(true);
    } catch (error) {
      toast.error('Failed to load details');
      console.error(error);
    }
  };

  // Export specific category to CSV
  const handleCategoryExport = () => {
    if (modalData && modalData.length > 0) {
      exportToCSV(modalData, `gems_${modalCategory}`);
    }
  };

  // Export all KPIs summary to CSV
  const exportAllKpis = () => {
    const summaryData = [
      { Category: 'Filter Mode', Metric: 'Data Mode', Value: includeAll ? 'All Accounts' : 'Real Users Only' },
      { Category: 'User Metrics', Metric: 'Total Users', Value: overview?.total_users || 0 },
      { Category: 'User Metrics', Metric: 'Daily Active Users', Value: overview?.dau || 0 },
      { Category: 'User Metrics', Metric: 'Monthly Active Users', Value: overview?.mau || 0 },
      { Category: 'User Metrics', Metric: 'New Signups Today', Value: overview?.new_signups_today || 0 },
      { Category: 'User Metrics', Metric: 'New Signups This Week', Value: overview?.new_signups_week || 0 },
      { Category: 'User Metrics', Metric: 'Retention Rate', Value: `${overview?.retention_rate || 0}%` },
      { Category: 'App Installs', Metric: 'iPhone & iPad installs (App Store + TestFlight)', Value: appStats?.installs?.ios?.total ?? 0 },
      { Category: 'App Installs', Metric: 'Android installs (Google Play + testing tracks)', Value: appStats?.installs?.android?.total ?? 0 },
      { Category: 'App Installs', Metric: 'Total app installs', Value: appStats?.installs?.total ?? 0 },
      { Category: 'App Installs', Metric: `New installs (${period} days)`, Value: appStats?.installs?.new_in_period ?? 0 },
      { Category: 'App Downloads', Metric: 'Google Play Clicks (Period)', Value: appStats?.google_play?.clicks_in_period || 0 },
      { Category: 'App Downloads', Metric: 'Google Play Clicks (All Time)', Value: appStats?.google_play?.total_clicks || 0 },
      { Category: 'App Downloads', Metric: 'Apple Store Clicks (Period)', Value: appStats?.apple_store?.clicks_in_period || 0 },
      { Category: 'App Downloads', Metric: 'Apple Store Clicks (All Time)', Value: appStats?.apple_store?.total_clicks || 0 },
      { Category: 'App Downloads', Metric: 'Total Clicks (Period)', Value: appStats?.total_clicks_in_period || 0 },
      { Category: 'App Downloads', Metric: 'Total Clicks (All Time)', Value: appStats?.total_clicks_all_time || 0 },
      { Category: 'App Downloads', Metric: 'iOS App Users', Value: appStats?.app_users?.ios || 0 },
      { Category: 'App Downloads', Metric: 'Android App Users', Value: appStats?.app_users?.android || 0 },
      { Category: 'App Downloads', Metric: `New App Users (${period} days)`, Value: appStats?.app_users?.new_in_period || 0 },
      { Category: 'Stream Metrics', Metric: 'Active Streams Now', Value: overview?.active_streams || 0 },
      { Category: 'Stream Metrics', Metric: 'Streams Today', Value: overview?.streams_today || 0 },
      { Category: 'Stream Metrics', Metric: `Total Streams (${period} days)`, Value: streamKpis?.total_streams || 0 },
      { Category: 'Stream Metrics', Metric: 'Total Viewers', Value: streamKpis?.total_viewers || 0 },
      { Category: 'Stream Metrics', Metric: 'Peak Concurrent Viewers', Value: streamKpis?.peak_concurrent_viewers || 0 },
      { Category: 'Stream Metrics', Metric: 'Avg Duration (min)', Value: streamKpis?.avg_stream_duration_minutes || 0 },
      { Category: 'Game Metrics', Metric: 'Games Today', Value: overview?.games_today || 0 },
      { Category: 'Game Metrics', Metric: `Total Games (${period} days)`, Value: gameKpis?.total_games || 0 },
      { Category: 'Game Metrics', Metric: 'Completed Games', Value: gameKpis?.completed_games || 0 },
      { Category: 'Game Metrics', Metric: 'Completion Rate', Value: `${gameKpis?.completion_rate || 0}%` },
      { Category: 'Game Metrics', Metric: 'Total Entries', Value: gameKpis?.total_wager_amount || 0 },
    ];
    
    exportToCSV(summaryData, 'gems_kpi_summary');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  const inst = appStats?.installs;   // Round 40: per-device install counts (undefined until the server has them)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Click on any metric to view details and export</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={exportAllKpis}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export All (CSV)
          </button>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="kpi-filter-toggle">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Data Filter:</span>
        </div>
        <button
          onClick={() => setIncludeAll(false)}
          data-testid="kpi-filter-real-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            !includeAll ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Eye className="w-4 h-4" />
          Real Users Only
        </button>
        <button
          onClick={() => setIncludeAll(true)}
          data-testid="kpi-filter-all-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            includeAll ? 'bg-gray-700 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <EyeOff className="w-4 h-4" />
          Include Test / Internal / Bot / Official / QA
        </button>
        <span className={`ml-auto text-xs font-medium px-3 py-1 rounded-full ${
          !includeAll ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
        }`}>
          {!includeAll ? 'Investor-Ready Metrics' : 'All Accounts (Debug)'}
        </span>
      </div>

      {/* User Metrics */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            User Metrics
          </h2>
          <button
            onClick={() => fetchMetricDetails('total_users', 'All Users')}
            className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            View all users <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Users"
            value={overview?.total_users?.toLocaleString() || '0'}
            icon={Users}
            color="emerald"
            onClick={() => fetchMetricDetails('total_users', 'Total Users')}
          />
          <MetricCard
            title="Daily Active Users"
            value={overview?.dau?.toLocaleString() || '0'}
            subtitle="Active today"
            icon={Activity}
            color="blue"
            onClick={() => fetchMetricDetails('dau', 'Daily Active Users')}
          />
          <MetricCard
            title="Monthly Active Users"
            value={overview?.mau?.toLocaleString() || '0'}
            subtitle="Last 30 days"
            icon={TrendingUp}
            color="emerald"
            onClick={() => fetchMetricDetails('mau', 'Monthly Active Users')}
          />
          <MetricCard
            title="Retention Rate"
            value={`${overview?.retention_rate || 0}%`}
            subtitle="7-day retention"
            icon={BarChart3}
            color="rose"
            onClick={() => fetchMetricDetails('retention_rate', 'Retention Analysis')}
          />
        </div>
      </div>

      {/* App Installs Section (Round 40) */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            App Installs
          </h2>
          <div className="flex items-center gap-4">
            <a
              href="/app-store"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              Configure Store Links
            </a>
            <button
              onClick={() => fetchMetricDetails('app_installs', 'App Installs by Store')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Install details <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Round 40: installs per device. Each store's doors count together —
              App Store + TestFlight, Play production + testing tracks. The old
              "clicks" were taps on the website's Get-the-app buttons only. */}
          <MetricCard
            title="iPhone & iPad Installs"
            value={(inst?.ios?.total ?? appStats?.app_users?.ios ?? 0).toLocaleString()}
            subtitle={`App Store + TestFlight · ${inst?.ios?.new_in_period || 0} new in ${period} days`}
            icon={Download}
            color="blue"
            onClick={() => fetchMetricDetails('app_installs', 'App Installs by Store')}
          />
          <MetricCard
            title="Android Installs"
            value={(inst?.android?.total ?? appStats?.app_users?.android ?? 0).toLocaleString()}
            subtitle={`Google Play + testing tracks · ${inst?.android?.new_in_period || 0} new in ${period} days`}
            icon={Download}
            color="emerald"
            onClick={() => fetchMetricDetails('app_installs', 'App Installs by Store')}
          />
          <MetricCard
            title="Total App Installs"
            value={(inst?.total ?? appStats?.app_users?.total ?? 0).toLocaleString()}
            subtitle={`${inst?.new_in_period ?? appStats?.app_users?.new_in_period ?? 0} new in ${period} days`}
            icon={BarChart3}
            color="rose"
            onClick={() => fetchMetricDetails('app_installs', 'App Installs by Store')}
          />
          <MetricCard
            title="Website 'Get the App' Taps"
            value={(appStats?.total_clicks_in_period || 0).toLocaleString()}
            subtitle={`${appStats?.total_clicks_all_time || 0} all time · Play ${appStats?.google_play?.total_clicks || 0} · App Store ${appStats?.apple_store?.total_clicks || 0}`}
            icon={TrendingUp}
            color="amber"
            onClick={() => fetchMetricDetails('app_downloads', 'Website Get-the-App Taps')}
          />
        </div>

        {/* Daily Download Clicks Chart */}
        {appStats?.daily_breakdown?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Daily Click Breakdown</h3>
              <button
                onClick={() => fetchMetricDetails('app_downloads', 'Daily Click Breakdown')}
                className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
              >
                Export <Download className="w-3 h-3" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Google Play</th>
                    <th className="py-2 px-3">Apple Store</th>
                    <th className="py-2 px-3">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {appStats.daily_breakdown.slice(-7).map((day, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm font-medium text-gray-900">{day.date}</td>
                      <td className="py-2 px-3 text-sm text-emerald-600">{day.google_play || 0}</td>
                      <td className="py-2 px-3 text-sm text-blue-600">{day.apple_store || 0}</td>
                      <td className="py-2 px-3 text-sm font-semibold text-gray-900">
                        {(day.google_play || 0) + (day.apple_store || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Signup & Engagement Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Signup Overview</h3>
            <button
              onClick={() => fetchMetricDetails('new_signups', 'New Signups')}
              className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              Export <Download className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-4">
            <div 
              className="flex items-center justify-between py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
              onClick={() => fetchMetricDetails('new_signups', 'New Signups Today')}
            >
              <span className="text-gray-600">New Signups Today</span>
              <span className="font-semibold text-gray-900 text-lg">{overview?.new_signups_today || 0}</span>
            </div>
            <div 
              className="flex items-center justify-between py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
              onClick={() => fetchMetricDetails('new_signups', 'New Signups This Week')}
            >
              <span className="text-gray-600">New Signups This Week</span>
              <span className="font-semibold text-gray-900 text-lg">{overview?.new_signups_week || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-gray-600">Avg Daily Signups (30d)</span>
              <span className="font-semibold text-emerald-600 text-lg">
                {overview?.avg_daily_signups || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">User Engagement</h3>
            <button
              onClick={() => fetchMetricDetails('retention_rate', 'Engagement Metrics')}
              className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              Export <Download className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <span className="text-gray-600">DAU/MAU Ratio</span>
              <span className="font-semibold text-gray-900 text-lg">
                {overview?.mau ? `${Math.round((overview?.dau / overview?.mau) * 100)}%` : '0%'}
              </span>
            </div>
            <div 
              className="flex items-center justify-between py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
              onClick={() => fetchMetricDetails('active_streams', 'Active Streams')}
            >
              <span className="text-gray-600">Active Streams Now</span>
              <span className="font-semibold text-rose-600 text-lg">{overview?.active_streams || 0}</span>
            </div>
            <div 
              className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
              onClick={() => fetchMetricDetails('games_today', 'Games Played Today')}
            >
              <span className="text-gray-600">Games Played Today</span>
              <span className="font-semibold text-amber-600 text-lg">{overview?.games_today || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stream Metrics */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Video className="w-5 h-5 text-rose-600" />
            Stream Metrics ({period} days)
          </h2>
          <button
            onClick={() => fetchMetricDetails('total_streams', 'All Streams')}
            className="text-sm text-rose-600 hover:text-rose-700 flex items-center gap-1"
          >
            View all streams <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Streams"
            value={streamKpis?.total_streams?.toLocaleString() || '0'}
            icon={Video}
            color="rose"
            onClick={() => fetchMetricDetails('total_streams', 'Total Streams')}
          />
          <MetricCard
            title="Total Viewers"
            value={streamKpis?.total_viewers?.toLocaleString() || '0'}
            icon={Users}
            color="blue"
            onClick={() => fetchMetricDetails('total_streams', 'Stream Viewers')}
          />
          <MetricCard
            title="Peak Concurrent"
            value={streamKpis?.peak_concurrent_viewers?.toLocaleString() || '0'}
            subtitle="Max viewers at once"
            icon={TrendingUp}
            color="emerald"
            onClick={() => fetchMetricDetails('total_streams', 'Peak Viewers')}
          />
          <MetricCard
            title="Avg Duration"
            value={`${Math.round(streamKpis?.avg_stream_duration_minutes || 0)}m`}
            subtitle="Per stream"
            icon={Activity}
            color="amber"
            onClick={() => fetchMetricDetails('total_streams', 'Stream Durations')}
          />
        </div>

        {/* Top Streamers */}
        {streamKpis?.top_streamers?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Top Streamers</h3>
              <button
                onClick={() => fetchMetricDetails('top_streamers', 'Top Streamers')}
                className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
              >
                Export <Download className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-3">
              {streamKpis.top_streamers.slice(0, 5).map((streamer, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2"
                  onClick={() => fetchMetricDetails('top_streamers', 'Top Streamers')}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-amber-100 text-amber-600' :
                      i === 1 ? 'bg-gray-100 text-gray-600' :
                      i === 2 ? 'bg-orange-100 text-orange-600' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{streamer.user?.name}</p>
                      <p className="text-sm text-gray-500">@{streamer.user?.handle}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-900">{streamer.stream_count} streams</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Game Metrics */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-amber-600" />
            Game Metrics ({period} days)
          </h2>
          <button
            onClick={() => fetchMetricDetails('total_games', 'All Games')}
            className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
          >
            View all games <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Games"
            value={gameKpis?.total_games?.toLocaleString() || '0'}
            icon={Gamepad2}
            color="amber"
            onClick={() => fetchMetricDetails('total_games', 'Total Games')}
          />
          <MetricCard
            title="Completed Games"
            value={gameKpis?.completed_games?.toLocaleString() || '0'}
            icon={Activity}
            color="emerald"
            onClick={() => fetchMetricDetails('completed_games', 'Completed Games')}
          />
          <MetricCard
            title="Completion Rate"
            value={`${gameKpis?.completion_rate || 0}%`}
            icon={PieChart}
            color="blue"
            onClick={() => fetchMetricDetails('total_games', 'Game Completion Stats')}
          />
          <MetricCard
            title="Total Entries"
            value={<span className="flex items-center gap-1">{(gameKpis?.total_wager_amount || 0).toLocaleString()} <EmeraldIcon className="h-6 w-6 inline" /></span>}
            subtitle="Emeralds entered"
            icon={TrendingUp}
            color="rose"
            onClick={() => fetchMetricDetails('total_games', 'Entry Statistics')}
          />
        </div>

        {/* Games by Phase */}
        {gameKpis?.games_by_phase && Object.keys(gameKpis.games_by_phase).length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Games by Status</h3>
              <button
                onClick={() => fetchMetricDetails('total_games', 'Games by Status')}
                className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
              >
                Export <Download className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(gameKpis.games_by_phase).map(([phase, count]) => (
                <div 
                  key={phase} 
                  className="text-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => fetchMetricDetails('total_games', `Games - ${phase}`)}
                >
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500 capitalize">{phase}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <DetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        data={modalData}
        category={modalCategory}
        onExport={handleCategoryExport}
      />
    </div>
  );
};

export default KPIPage;
